import React, { useEffect, useState } from "react";
import LinkedUserPicker from "./LinkedUserPicker";
import { FaTimes } from "react-icons/fa";
import api from "../../../services/apiService";
import { useBackToClose } from "../../../utils/backToClose";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const EMPTY = {
  fullName: "", email: "", phone: "", cnic: "", address: "",
  joiningDate: "", employmentType: "", workMode: "", status: "active",
  locationId: "", userId: "", employeeCode: "",
  designation: "", department: "",
};

const LABEL = "mb-1 block text-sm font-medium text-app-secondary";
const INPUT = "w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20";

const mapEmployeeToForm = (d) => ({
  fullName: d.fullName || "", email: d.email || "", phone: d.phone || "",
  cnic: d.cnic || "", address: d.address || "",
  joiningDate: d.joiningDate || "", employmentType: d.employmentType || "",
  workMode: d.workMode || "", status: d.status || "active",
  locationId: d.locationId || "", userId: d.userId || "",
  employeeCode: d.employeeCode || "",
  designation: d.designation || "", department: d.department || "",
});

function HrEmployeeModal({ show, onClose, onSubmit, employeeId = null, locations = [], isSubmitting = false, error = "" }) {
  useBackToClose(show, onClose);
  const isEdit = Boolean(employeeId);
  const [form, setForm] = useState({ ...EMPTY });
  const [isLoading, setIsLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState("");

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!show) return;
    if (!isEdit) {
      api.get("/hr/employees/next-code", { headers: authHeaders() })
         .then((r) => set("employeeCode", r.data.employeeCode))
         .catch(() => {});
      return;
    }
    api.get(`/hr/employees/${employeeId}`, { headers: authHeaders() })
       .then((r) => setForm(mapEmployeeToForm(r.data)))
       .catch(() => setLoadError("Failed to load employee."))
       .finally(() => setIsLoading(false));
  }, [show, isEdit, employeeId]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) return;
    onSubmit({ ...form, locationId: form.locationId || null, userId: form.userId || null });
  };

  const message = error || loadError;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60 p-4">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0" />
      <div role="dialog" aria-modal="true" aria-labelledby="hr-employee-modal-title" className="relative w-full max-w-2xl rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50">
        <div className="flex items-center justify-between border-b border-app-primary px-6 py-4">
          <h3 id="hr-employee-modal-title" className="text-lg font-semibold text-app-heading">{isEdit ? "Edit Employee" : "Add Employee"}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40">
            <FaTimes />
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-16 text-app-muted">Loading…</div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
            {message && <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{message}</p>}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="ef-code" className={LABEL}>Employee Code</label>
                <input id="ef-code" readOnly value={form.employeeCode}
                  className="w-full rounded-lg border border-app-primary bg-app-secondary px-3 py-2 text-sm text-app-muted cursor-not-allowed" />
              </div>
              <div>
                <label htmlFor="ef-name" className={LABEL}>Full Name <span className="text-app-error">*</span></label>
                <input id="ef-name" required value={form.fullName} onChange={(e) => set("fullName", e.target.value)} className={INPUT} />
              </div>
              <div>
                <label htmlFor="ef-designation" className={LABEL}>Designation</label>
                <input id="ef-designation" maxLength={150} value={form.designation} onChange={(e) => set("designation", e.target.value)} className={INPUT} placeholder="e.g. Research Analyst" />
              </div>
              <div>
                <label htmlFor="ef-department" className={LABEL}>Department</label>
                <input id="ef-department" maxLength={150} value={form.department} onChange={(e) => set("department", e.target.value)} className={INPUT} placeholder="e.g. Operations" />
              </div>
              <div>
                <label htmlFor="ef-email" className={LABEL}>Email</label>
                <input id="ef-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT} />
              </div>
              <div>
                <label htmlFor="ef-phone" className={LABEL}>Phone</label>
                <input id="ef-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT} />
              </div>
              <div>
                <label htmlFor="ef-cnic" className={LABEL}>CNIC</label>
                <input id="ef-cnic" value={form.cnic} onChange={(e) => set("cnic", e.target.value)} className={INPUT} />
              </div>
              <div>
                <label htmlFor="ef-joining" className={LABEL}>Joining Date</label>
                <input id="ef-joining" type="date" value={form.joiningDate} onChange={(e) => set("joiningDate", e.target.value)} className={INPUT} />
              </div>
              <div>
                <label htmlFor="ef-location" className={LABEL}>Location</label>
                <select id="ef-location" value={form.locationId} onChange={(e) => set("locationId", e.target.value)} className={INPUT}>
                  <option value="">— None —</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="ef-emp-type" className={LABEL}>Employment Type</label>
                <select id="ef-emp-type" value={form.employmentType} onChange={(e) => set("employmentType", e.target.value)} className={INPUT}>
                  <option value="">— None —</option>
                  {["full_time","part_time","intern","contractor"].map((v) => (
                    <option key={v} value={v}>{v.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="ef-work-mode" className={LABEL}>Work Mode</label>
                <select id="ef-work-mode" value={form.workMode} onChange={(e) => set("workMode", e.target.value)} className={INPUT}>
                  <option value="">— None —</option>
                  {["onsite","remote","hybrid"].map((v) => (
                    <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="ef-status" className={LABEL}>Status</label>
                <select id="ef-status" value={form.status} onChange={(e) => set("status", e.target.value)} className={INPUT}>
                  {["active","probation","resigned","terminated"].map((v) => (
                    <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <span className={LABEL}>Linked user account</span>
              <LinkedUserPicker value={form.userId} onChange={(id) => set("userId", id)} employeeId={employeeId} />
            </div>
            <div>
              <label htmlFor="ef-address" className={LABEL}>Address</label>
              <textarea id="ef-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className={`${INPUT} resize-none`} />
            </div>
            <div className="flex justify-end gap-3 border-t border-app-primary pt-4">
              <button type="button" onClick={onClose}
                className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting || Boolean(loadError)}
                className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500">
                {isSubmitting ? "Saving…" : isEdit ? "Save Changes" : "Add Employee"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default HrEmployeeModal;
